import { useState } from "react";
import { User, Phone, MapPin, Loader2, Check, AlertCircle } from "lucide-react";

const CustomForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    nome_cognome: "",
    telefono: "",
    indirizzo: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome_cognome.trim()) {
      newErrors.nome_cognome = "Il nome è obbligatorio";
    } else if (formData.nome_cognome.trim().length < 3) {
      newErrors.nome_cognome = "Inserisci nome e cognome completi";
    }
    
    if (!formData.telefono.trim()) {
      newErrors.telefono = "Il telefono è obbligatorio";
    } else if (!/^[\d\s+()-]{8,}$/.test(formData.telefono.trim())) {
      newErrors.telefono = "Inserisci un numero valido";
    }
    
    if (!formData.indirizzo.trim()) {
      newErrors.indirizzo = "L'indirizzo è obbligatorio";
    } else if (formData.indirizzo.trim().length < 10) {
      newErrors.indirizzo = "Inserisci indirizzo completo con città e CAP";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Fire InitiateCheckout pixel event
    if (typeof window.fbq === "function") {
      window.fbq("track", "InitiateCheckout", {
        content_name: "Tauro Plus",
        content_category: "Integratori",
        value: 49.00,
        currency: "EUR"
      });
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          nome_cognome: formData.nome_cognome.trim(),
          telefono: formData.telefono.trim(),
          indirizzo: formData.indirizzo.trim(),
          source: "customform"
        })
      });
      
      if (response.ok) {
        // Fire CompleteRegistration pixel event
        if (typeof window.fbq === "function") {
          window.fbq("track", "CompleteRegistration", {
            content_name: "Tauro Plus Lead",
            value: 49.00,
            currency: "EUR"
          });
        }
        
        setSubmitStatus("success");
        setFormData({ nome_cognome: "", telefono: "", indirizzo: "" });
        if (onSuccess) onSuccess();
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === "success") {
    return (
      <div className="bg-green-900/30 border-2 border-green-500 rounded-sm p-8 text-center" data-testid="form-success">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">ORDINE RICEVUTO!</h3>
        <p className="text-green-400 font-bold mb-4">Grazie per il tuo ordine!</p>
        <p className="text-gray-300">
          Un nostro operatore ti contatterà a breve per confermare i dettagli della spedizione.
        </p>
        <p className="text-gray-400 text-sm mt-4">
          Riceverai Tauro Plus in 24/48 ore. Pagherai comodamente al corriere.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-700 rounded-sm p-6" data-testid="custom-order-form">
      <h3 className="text-xl font-bold text-white text-center mb-6 uppercase">
        Compila per Ricevere Tauro Plus
      </h3>
      
      {/* Nome e Cognome */}
      <div className="mb-4">
        <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="nome_cognome">
          Nome e Cognome *
        </label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            id="nome_cognome"
            name="nome_cognome"
            value={formData.nome_cognome}
            onChange={handleChange}
            placeholder="Mario Rossi"
            className={`w-full bg-zinc-800 border ${errors.nome_cognome ? 'border-red-500' : 'border-zinc-600'} rounded-sm py-3 pl-11 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors`}
            data-testid="input-nome"
          />
        </div>
        {errors.nome_cognome && (
          <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.nome_cognome}
          </p>
        )}
      </div>
      
      {/* Telefono */}
      <div className="mb-4">
        <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="telefono">
          Numero di Telefono *
        </label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="tel"
            id="telefono"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            placeholder="+39 333 1234567"
            className={`w-full bg-zinc-800 border ${errors.telefono ? 'border-red-500' : 'border-zinc-600'} rounded-sm py-3 pl-11 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors`}
            data-testid="input-telefono"
          />
        </div>
        {errors.telefono && (
          <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.telefono}
          </p>
        )}
      </div>
      
      {/* Indirizzo */}
      <div className="mb-6">
        <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="indirizzo">
          Indirizzo di Spedizione *
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
          <textarea
            id="indirizzo"
            name="indirizzo"
            value={formData.indirizzo}
            onChange={handleChange}
            placeholder="Via Roma 123, 00100 Roma (RM)"
            rows={2}
            className={`w-full bg-zinc-800 border ${errors.indirizzo ? 'border-red-500' : 'border-zinc-600'} rounded-sm py-3 pl-11 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors resize-none`}
            data-testid="input-indirizzo"
          />
        </div>
        {errors.indirizzo && (
          <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.indirizzo}
          </p>
        )}
      </div>
      
      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-4 px-6 rounded-sm uppercase text-lg transition-all duration-200 flex items-center justify-center gap-2"
        data-testid="submit-order-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            INVIO IN CORSO...
          </>
        ) : (
          <>
            ORDINA ORA - PAGA ALLA CONSEGNA
          </>
        )}
      </button>
      
      {submitStatus === "error" && (
        <p className="text-red-400 text-sm mt-3 text-center flex items-center justify-center gap-1">
          <AlertCircle className="w-4 h-4" />
          Errore nell'invio. Riprova o contattaci.
        </p>
      )}
      
      {/* Trust badges under form */}
      <div className="mt-6 grid grid-cols-2 gap-2 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500" />
          <span>Spedizione Gratis</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500" />
          <span>Paga al Corriere</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500" />
          <span>Pacco Anonimo</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500" />
          <span>Garanzia 90 Giorni</span>
        </div>
      </div>
    </form>
  );
};

export default CustomForm;
